'use client';

import {
    ConfigurableProductOptions,
    ConfigurableProductOptionsValues,
    ConfigurableVariant,
    Maybe,
} from '__generated__/__types__';
import React, {createContext, useContext, useMemo, useReducer, PropsWithChildren} from 'react';
import {AttributesState, attributesReducer, initialState} from './attributes.reducer';

interface AttributesProviderState extends AttributesState {
    handleSelectAttribute: (key: string, values: ConfigurableProductOptionsValues) => void;
    setInitialData: (
        productOptions: Maybe<ConfigurableProductOptions>[],
        variants: Maybe<ConfigurableVariant>[],
    ) => void;
    setIsSelected: (isSelected: boolean) => void;
}
export const AttributesContext = createContext<AttributesProviderState | undefined>(undefined);

AttributesContext.displayName = 'AttributesContext';

export const useAttributes = () => {
    const context = useContext(AttributesContext);
    if (context === undefined) {
        throw new Error(`useAttributes must be used within a AttributesProvider`);
    }
    return useMemo(() => context, [context]);
};

export const AttributesProvider = ({children}: PropsWithChildren) => {
    const [state, dispatch] = useReducer(attributesReducer, initialState);

    const handleSelectAttribute = (key: string, values: ConfigurableProductOptionsValues) => {
        const newAttribute = {
            [key]: values,
        };
        dispatch({type: 'SET_ATTRIBUTES', attributes: newAttribute});
    };

    const setInitialData = (
        productOptions: Maybe<ConfigurableProductOptions>[],
        variants: Maybe<ConfigurableVariant>[],
    ) => {
        const validProductOptions = productOptions.filter(option => !!option) as ConfigurableProductOptions[];
        const validVariants = variants.filter(variant => !!variant) as ConfigurableVariant[];

        dispatch({type: 'SET_INITIAL_DATA', productOptions: validProductOptions, variants: validVariants});
    };

    const setIsSelected = (isSelected: boolean) => {
        dispatch({type: 'SET_IS_SELECTED', isSelected: isSelected});
    };

    const value = useMemo(
        () => ({
            ...state,
            handleSelectAttribute,
            setInitialData,
            setIsSelected,
        }),
        [state],
    );

    return <AttributesContext.Provider value={value}>{children}</AttributesContext.Provider>;
};
